import React, { useEffect, useState } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { updateConnectionMeta } from "../Shared/NameTagEdit/updateConnectionMeta";
import { DetailLink } from "../Shared/DetailLink";
import { cpeDetailPath, extenderPopStatusPresentation } from "./ExtenderPopData";
import { RoleTag } from "./ExtenderPopDetailShared";
import { apiErrorMessage } from "./extenderPopCarrierAccessData";
import type { ExtenderPopCpeDetail } from "./extenderPopDetail.types";

interface RouterNameProps {
  extenderPopId: number;
  tenantId: number;
  cpe: ExtenderPopCpeDetail;
  onError: (message: string) => void;
}

/** A device's name, edited in place with its pencil, like the CPE's. */
const RouterName: React.FC<RouterNameProps> = ({
  extenderPopId,
  tenantId,
  cpe,
  onError,
}) => {
  const api = useAxiosAuth0();
  const [name, setName] = useState(cpe.name ?? "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => setName(cpe.name ?? ""), [cpe.name]);

  const save = async (value: string) => {
    const next = value.trim();
    // A device always has a name: an empty one keeps the current.
    if (!next || next === name) return;
    const previous = name;
    setName(next);
    setIsSaving(true);
    try {
      await updateConnectionMeta(api, {
        kind: "extender-pop-cpe",
        resourceId: String(cpe.id),
        parentId: String(extenderPopId),
        tenantId,
        data: { name: next },
      });
    } catch (error) {
      setName(previous);
      onError(apiErrorMessage(error, "Could not rename the device"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <GenericTag
      label={name || "—"}
      variant="neutral"
      size="small"
      minWidth="100px"
      editable={true}
      value={name}
      onSave={save}
      isSaving={isSaving}
      disabled={isSaving}
    />
  );
};

/**
 * The CPE's devices (its routers), one row each: name (editable), role,
 * status and the way into the device's detail. The CPE goes by one id and
 * holds the contract, both shown above; the devices go by their names. This
 * is where an HA pair reads as a pair.
 */
const CpesCard: React.FC<{
  extenderPopId: number;
  tenantId: number;
  cpes: ExtenderPopCpeDetail[];
}> = ({ extenderPopId, tenantId, cpes }) => {
  const [error, setError] = useState<string | null>(null);

  return (
    <GenericCard
      isCollapsible={false}
      headerContent="CPE Devices"
      bodyContent={
        cpes.length === 0 ? (
          <p className="py-1.5 text-sm text-semantic-text-secondary">
            No device assigned to this CPE yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            {error && <p className="text-sm text-semantic-text-error">{error}</p>}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-semantic-text-secondary">
                  <th className="py-1.5 pr-4 font-normal">Name</th>
                  <th className="py-1.5 pr-4 font-normal">Role</th>
                  <th className="py-1.5 pr-4 font-normal">Status</th>
                  <th className="py-1.5 font-normal" />
                </tr>
              </thead>
              <tbody>
                {cpes.map((cpe) => {
                  const status = extenderPopStatusPresentation(cpe.status);
                  return (
                    <tr
                      key={cpe.id}
                      className="border-t border-semantic-border-secondary text-semantic-text-primary"
                    >
                      <td className="py-1.5 pr-4">
                        <RouterName
                          extenderPopId={extenderPopId}
                          tenantId={tenantId}
                          cpe={cpe}
                          onError={setError}
                        />
                      </td>
                      <td className="py-1.5 pr-4">
                        <RoleTag role={cpe.role} />
                        {!cpe.role && "-"}
                      </td>
                      <td className="py-1.5 pr-4">
                        <GenericTag
                          variant={status.variant}
                          label={status.label}
                          size="small"
                        />
                      </td>
                      <td className="py-1.5 text-right">
                        <DetailLink to={cpeDetailPath(extenderPopId, cpe.id)} label="Details" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      }
    />
  );
};

export default CpesCard;
